// When an event is, in the several shapes the site needs to say it.
//
// Shared by the attendee page, the campus map and the creator, because those
// three disagreeing about what time an event starts is the one bug nobody would
// think to check for.

/** "2026-09-14" + "18:30" → a Date, or null. Local time, which is what a poster means. */
export function eventStart(e) {
  if (!e?.date) return null;
  const d = new Date(`${e.date}T${e.time || '00:00'}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── Reading and writing a clock time ────────────────────────────────────────
// The creator's time control takes an hour, minutes and am/pm rather than a
// native time input, whose am/pm segment is TYPED (press A or P) and whose
// minutes start empty although club events start on the hour. These three
// functions are the whole of its arithmetic, and they live here because getting
// a 12-hour clock wrong is silent: an event published at 7am instead of 7pm
// reads perfectly well right up until nobody comes.

/** "18:30" → { h12: 6, m: 30, pm: true }. Anything unparseable is all nulls. */
export function parseHm(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value || ''));
  if (!m) return { h12: null, m: null, pm: null };
  const h = Number(m[1]), min = Number(m[2]);
  if (!(h >= 0 && h <= 23) || !(min >= 0 && min <= 59)) return { h12: null, m: null, pm: null };
  // ⚠️ Both 0 and 12 are "12" on a clock face — midnight AM and noon PM.
  return { h12: h % 12 === 0 ? 12 : h % 12, m: min, pm: h >= 12 };
}

/** The inverse: 6, 30, PM → "18:30". Midnight is "00:00", noon is "12:00". */
export const toHm = (h12, m, pm) =>
  `${String((h12 % 12) + (pm ? 12 : 0)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

/**
 * Which half of the day a freshly-picked hour lands in.
 *
 * With an `after` time — an end time being set against a start — it is whichever
 * reading puts it AFTER the start: a 6pm event that "ends at 7" ends at 7pm, and
 * one that "ends at 1" ends at 1 in the morning. Answering 7am there would be an
 * event finishing eleven hours before it began.
 *
 * With nothing to go on: noon and 1–7 read as afternoon or evening, 8–11 as
 * morning. That is roughly when club events happen, and the guess is always
 * visible in the two buttons — which is what makes guessing safe at all.
 */
export function guessPm(h12, min, after) {
  const start = parseHm(after);
  if (start.h12 == null) return h12 === 12 || h12 < 8;
  const startMin = ((start.h12 % 12) + (start.pm ? 12 : 0)) * 60 + start.m;
  const am = (h12 % 12) * 60 + min;
  const pm = ((h12 % 12) + 12) * 60 + min;
  if (am > startMin) return false;          // the soonest reading still after it
  if (pm > startMin) return true;
  return !start.pm;                         // nothing later today — an evening
}                                           // event "ending at 1" ends at 1am

/** What an event with no end time is assumed to fill. */
const DEFAULT_LENGTH_MS = 2 * 60 * 60 * 1000;

/**
 * When an event is OVER — the one question "is this still on?" turns on.
 *
 * A club is not required to give an end time, so one that has not is assumed to
 * run a couple of hours: long enough that somebody walking in ten minutes late
 * still finds the event listed, short enough that yesterday's does not linger.
 *
 * An end time EARLIER than the start means the evening ran past midnight, which
 * is a real thing a club types and not a mistake to reject — it is the next day.
 */
export function eventEnd(e) {
  const start = eventStart(e);
  if (!start) return null;
  if (e?.endTime) {
    const d = new Date(`${e.date}T${e.endTime}`);
    if (!Number.isNaN(d.getTime())) {
      return d.getTime() >= start.getTime() ? d : new Date(d.getTime() + 24 * 60 * 60 * 1000);
    }
  }
  return new Date(start.getTime() + DEFAULT_LENGTH_MS);
}

/** Has it finished? An event with no date announced never has. */
export function isFinished(e, now = Date.now()) {
  const end = eventEnd(e);
  return end ? end.getTime() < now : false;
}

/** The long form, for a banner: "Tuesday, September 14 · 6:30 PM–8:00 PM". */
export function fmtWhen(e) {
  const d = eventStart(e);
  if (!d) return '';
  const day = d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  if (!e.time) return day;
  const t = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${t}${e.endTime ? `–${e.endTime}` : ''}`;
}

/** The short form, for a list row: "Tue, Sep 14 · 6:30 PM". */
export function fmtWhenShort(e) {
  const d = eventStart(e);
  if (!d) return 'Date to be announced';
  const day = d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  return e.time ? `${day} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : day;
}

/** Same calendar day, in local time — not "within 24 hours". */
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Is this event on today's date?
 *
 * The calendar day, not a 24-hour window: "today" is the day where the answer
 * somebody wants stops being a date and starts being a number of minutes.
 */
export function isToday(e, now = Date.now()) {
  const d = eventStart(e);
  return d ? sameDay(d, new Date(now)) : false;
}

/** "Sep 14", or "Sep 14, 2027" once the year stops being obvious. */
export function fmtDate(d, now = Date.now()) {
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString([], {
    month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/**
 * What a map pin says about when: a headline and a date under it.
 *
 * `text` is the line somebody reads first, and it changes register with how far
 * off the event is:
 *
 *  · "Now" for one that started in the last couple of hours — the event somebody
 *    is walking to right now.
 *  · A countdown on the day itself, which is the only day where minutes decide
 *    anything.
 *  · The full weekday name otherwise. "Sunday" is how somebody holds a plan in
 *    their head, where a date makes them work out which day that is.
 *
 * `sub` is always the exact date, small, underneath. The weekday says *which
 * day*, the date says *which Sunday* — a pin reading only "Sunday" is ambiguous
 * the moment there is more than one of them on the map, and there usually is.
 *
 * `tone` is for colour ('now' | 'today' | '').
 *
 * ⚠️ NOTHING CALLS THIS RIGHT NOW. It was /eventmap's badge, and /eventmap is
 * retired (see `src/index.js`, which redirects it to /umanitoba). It is kept,
 * with its tests, because it is the two-line answer — the one for a screen
 * somebody opened to BROWSE what is on — and `pinLabel` below is deliberately
 * not that. If a browsing surface comes back, this is what it draws; deleting
 * it would mean the next one re-deriving the "which Sunday" reasoning from
 * scratch.
 */
export function pinWhen(e, now = Date.now()) {
  const d = eventStart(e);
  if (!d) return { text: '', sub: '', tone: '' };

  const ms = d.getTime() - now;
  const sub = fmtDate(d, now);

  // Under way: "Now" until its own end time, so a nine-to-five open house says
  // it is on at three rather than naming the weekday it started on.
  if (ms <= 0) {
    return isFinished(e, now)
      ? { text: d.toLocaleDateString([], { weekday: 'long' }), sub, tone: '' }
      : { text: 'Now', sub, tone: 'now' };
  }

  if (sameDay(d, new Date(now))) {
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return { text: `in ${mins}m`, sub, tone: 'now' };
    const h = Math.floor(mins / 60), m = mins % 60;
    return { text: m ? `in ${h}h ${m}m` : `in ${h}h`, sub, tone: 'today' };
  }

  return { text: d.toLocaleDateString([], { weekday: 'long' }), sub, tone: '' };
}

/**
 * What a pin on the CAMPUS MAP says about when — one short line, not two.
 *
 * Deliberately a different answer from `pinWhen` above, because it is answering
 * a different question. That one belonged to /eventmap — a page somebody opened
 * to browse what is on, where "Sunday" and the date under it are what you plan
 * around — and is now unused, that page having been retired into this one's.
 * This one belongs on the pathfinder, where an event pin is something
 * you came across while looking for a room: the pin has to say, in one glance
 * and one line, whether this is a thing happening *now* or a thing happening on
 * some other day.
 *
 * So:
 *  · today → the clock time, because on the day the date is not the question;
 *  · under way → "Now", since a start time already past reads as missed;
 *  · any other day → the date, which is the whole answer for a plan.
 *
 * Two lines and a countdown would be the map's most prominent object, and it is
 * not — it is something noticed on the way to a lecture.
 */
export function pinLabel(e, now = Date.now()) {
  const d = eventStart(e);
  if (!d) return { text: '', tone: '' };
  if (d.getTime() <= now) {
    return isFinished(e, now) ? { text: '', tone: '' } : { text: 'Now', tone: 'now' };
  }
  if (sameDay(d, new Date(now))) {
    return { text: d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }), tone: 'today' };
  }
  return { text: fmtDate(d, now), tone: '' };
}

/** An .ics file the browser can hand to any calendar app. */
export function icsFor(e) {
  const start = eventStart(e);
  if (!start) return null;
  const end = eventEnd(e);   // the club's own end time, or a sensible default block
  // UTC stamps, so the file means the same instant wherever it is opened.
  const z = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const where = [e.buildingName, e.room?.roomNumber ? `Room ${e.room.roomNumber}` : '', 'University of Manitoba']
    .filter(Boolean).join(', ');
  // Escaped per RFC 5545: a comma or semicolon in a name would otherwise end the
  // field and produce a file some calendars silently refuse.
  const esc = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/[,;]/g, m => `\\${m}`).replace(/\n/g, '\\n');
  const link = typeof window !== 'undefined' ? window.location.href : '';
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//UMaps//Events//EN', 'BEGIN:VEVENT',
    `UID:${e.id}@myumaps.ca`,
    `DTSTAMP:${z(new Date())}`,
    `DTSTART:${z(start)}`,
    `DTEND:${z(end)}`,
    `SUMMARY:${esc(e.name)}`,
    `LOCATION:${esc(where)}`,
    `DESCRIPTION:${esc(`Hosted by ${e.host || ''}. Directions: ${link}`)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
}

/** Hand the .ics to the browser as a download. */
export function downloadIcs(e) {
  const ics = icsFor(e);
  if (!ics) return;
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(e.name || 'event').replace(/[^A-Za-z0-9]+/g, '-')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}
